import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { brand } from '../../data/palette';
import type { Choice } from '../../types/filtek';
interface ChoiceListProps {
  choices: Choice[];
  selected?: string;
  accent?: string;
  onSelect: (choice: Choice) => void;
  onHover?: (choice?: Choice) => void;
  showIndex?: boolean;
}

/**
 * Large typographic selection rows. Hovering sweeps translucent material
 * across the row; selecting leaves the accent behind.
 */
export function ChoiceList({
  choices,
  selected,
  accent = brand.green,
  onSelect,
  onHover,
  showIndex = true
}: ChoiceListProps) {
  return <ul className="w-full border-y border-enamel/10" onMouseLeave={() => onHover?.(undefined)} data-id="element-399" data-mp="2dtv7jre3etup">
      {choices.map((choice, index) => {
      const isSelected = selected === choice.id;
      return <li key={choice.id} className="border-b border-enamel/10 last:border-b-0" data-id="element-400" data-mp="1yyp2p7qqh12y" data-mpi={index}>
            <button type="button" onClick={() => onSelect(choice)} onMouseEnter={() => onHover?.(choice)} onFocus={() => onHover?.(choice)} aria-pressed={isSelected} className="group relative flex w-full items-center gap-4 overflow-hidden py-4 pl-1 pr-2 text-left sm:gap-6 sm:py-5" data-id="element-401" data-mp="1z11fz7ljqn9o">
              <span className="pointer-events-none absolute inset-0 origin-left scale-x-0 bg-enamel/[0.05] transition-transform duration-[600ms] ease-out group-hover:scale-x-100" data-id="element-402" data-mp="1l1sf81d2kj55" />
              <motion.span className="pointer-events-none absolute inset-0 origin-left" style={{
            backgroundColor: accent
          }} initial={false} animate={{
            scaleX: isSelected ? 1 : 0,
            opacity: isSelected ? 0.18 : 0
          }} transition={{
            duration: 0.75,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-403" data-mp="2dov8of2w23oj" />
              <motion.span className="pointer-events-none absolute bottom-0 left-0 h-px origin-left" style={{
            backgroundColor: accent
          }} initial={false} animate={{
            scaleX: isSelected ? 1 : 0,
            opacity: isSelected ? 1 : 0
          }} transition={{
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-404" data-mp="sdd4vs4vup05" />

              {showIndex && <span className="label-mono relative z-10 w-6 shrink-0 text-[10px] text-mute/70" data-id="element-405" data-mp="2gbe4brn2bivc">
                  {String(index + 1).padStart(2, '0')}
                </span>}

              <span className="relative z-10 flex-1" data-id="element-406" data-mp="279ekysoks2p8">
                <span className="block font-light tracking-[-0.03em] text-[1.45rem] leading-tight transition-all duration-500 group-hover:translate-x-1.5 sm:text-[1.9rem] lg:text-[2.35rem]" style={{
              color: isSelected ? accent : 'var(--enamel)'
            }} data-id="element-407" data-mp="27c0wf3el72cq">
                  {choice.label}
                </span>
                {choice.detail && <span className="mt-1 block max-w-md text-sm font-light text-mute/80 lg:hidden" data-id="element-408" data-mp="3rnoq3e3e2eqa">
                    {choice.detail}
                  </span>}
              </span>

              {choice.detail && <span className="relative z-10 hidden max-w-[19rem] text-right text-sm font-light leading-snug text-mute/80 opacity-60 transition-opacity duration-500 group-hover:opacity-100 lg:block" data-id="element-409" data-mp="ctzhz6wpit9w">
                  {choice.detail}
                </span>}

              <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center" data-id="element-410" data-mp="3gcsqv6ejitxq">
                <ArrowRightIcon className="h-4 w-4 -translate-x-2 opacity-0 transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100" style={{
              color: isSelected ? accent : 'var(--enamel)'
            }} aria-hidden="true" data-id="element-411" data-mp="3lbcerccp2fsq" />
              </span>
            </button>
          </li>;
    })}
    </ul>;
}